import { KeyValue } from "@angular/common";
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { of, Subscription } from "rxjs";
import { SavingStateService } from "src/app/core/survey/shared/services/saving-state.service";
import { Floor } from "src/app/shared/Models/read/Floor";
import { ToastService } from "src/app/shared/toast.service";
import { UnitTypeEnum } from "../../../../../../shared/Models/enums/UnitUnitTypeEnum";
import { Building } from "../../../../../../shared/Models/read";
import {
  InsideSurveyInsideUnits,
  INSIDE_SURVEY_INSIDE_UNITS,
  INSIDE_SURVEY_OUTSIDE_UNITS,
} from "../../../config/inside-survey.magic-strings";
import { InsideSurveyTab } from "../../../models/insideSurveyTab.interface";
import { InsideUnit } from "../../../models/insideUnit.model";
import { ValidationError } from "../../../../../../shared/Models/ValidationError";
import { BuildingTypeEnum } from "../../../../../../shared/Models/enums/BuildingTypeEnum";
import { SurveyFrameTabTemplateComponent } from "src/app/core/survey/shared/survey-frame-list/survey-frame-tab-template/survey-frame-tab-template.component";
import { FormValidationService } from "src/app/core/survey/shared/services/formValidation.service";

@Component({
  selector: "app-inside-survey-inside-units",
  templateUrl: "./inside-survey-inside-units.component.html",
  styleUrls: ["./inside-survey-inside-units.component.scss"],
})
export class InsideSurveyInsideUnitsComponent
  implements OnInit, AfterViewInit, InsideSurveyTab
{
  tabId: string = INSIDE_SURVEY_INSIDE_UNITS;
  @Input() insideUnits: InsideUnit[][];
  @Input() buildings: Building[];
  @Input() isAuthCanWrite: boolean;
  @Input() insideSurveyId: string;

  @ViewChild("tabs") tabsComponent: SurveyFrameTabTemplateComponent;

  insideUnitVm: { type: string; units: InsideUnit[] }[];
  showAddUnits = false;
  newUnitType: UnitTypeEnum;
  unitPopUpForm: FormGroup = new FormGroup({});
  popUpFormSubscription = Subscription;
  emptyUnitTypes: string[];
  totalNumberOfUnits: number;

  validationBuildings: any[];

  buildingTypeEnum = BuildingTypeEnum;
  insideSurveyInsideUnits = InsideSurveyInsideUnits;

  constructor(
    private _toastService: ToastService,
    private _savingStateService: SavingStateService,
    private formValidationService: FormValidationService
  ) {}

  ngOnInit() {
    this.buildings.forEach((b) => {
      this.formValidationService.addFormError(
        [INSIDE_SURVEY_INSIDE_UNITS, b.id],
        {
          [InsideSurveyInsideUnits.FLOORS_FIELD]: false,
        }
      );
    });
    this.buildValidationBuildings();
  }

  ngAfterViewInit(): void {
    this.tabsComponent?.onToggleDetail(this.buildings?.[0]?.id);
  }

  get contexts() {
    return this.buildings.map((b, i) => ({ id: b.id, index: i, building: b }));
  }

  buildValidationBuildings() {
    this.validationBuildings = this.buildings.map((b) => ({
      id: b.id,
      floors: Object.entries(b.floors).reduce(
        (acc, e) => ({
          ...acc,
          [+e[0]]: {
            floorNumber: e[1].floorNumber,
            totalNumberOfUnits: e[1].totalNumberOfUnits,
          } as Floor,
        }),
        {}
      ),
      floorsBelowGround: b.floorsBelowGround,
    }));
  }

  totalUnits(building: Building) {
    return Object.values(building.floors).reduce(
      (u: number, i: Floor) => (u += i.totalNumberOfUnits),
      0
    );
  }

  totalUnitsOutside(building: Building) {
    return (
      building.outsideSurvey.numberOfBusinessUnits +
      building.outsideSurvey.numberOfLivingUnits +
      building.outsideSurvey.numberOfServiceUnits
    );
  }

  floorsBelowGroundChanged(amountOfFloor: number, building: Building) {
    building.floorsBelowGround = amountOfFloor;
    building.buildFloors();
  }

  save(checkValidity?: boolean) {
    if (checkValidity) {
      const error = this.getValidationError();
      if (error) {
        this._toastService.showWarnToast(error.title, error.message);
        return of(false);
      }
    }
    if (!this.detectChanges()) return of(true);
    if (this.isAuthCanWrite) {
      this.buildValidationBuildings();
      return this._savingStateService.addInsideSurveyInsideUnits({
        buildings: this.buildings.map((s) => {
          s.buildFloors();
          return {
            buildingId: s.id,
            floors: Object.keys(s.floors).map((r) => {
              return {
                floorNumber: +r,
                totalNumberOfUnits: s.floors[r].totalNumberOfUnits ?? 0,
              };
            }),
          };
        }),
      });
    }
  }

  detectChanges() {
    return this.validationBuildings.some((v) => {
      var b = this.buildings.find((i) => i.id == v.id);
      return (
        v.floorsBelowGround != b.floorsBelowGround ||
        Object.values(b.floors).some(
          (f) =>
            !v.floors[f.floorNumber] ||
            v.floors[f.floorNumber].totalNumberOfUnits != f.totalNumberOfUnits
        )
      );
    });
  }

  getErrors(building?: string, field?: string) {
    return this.formValidationService.getErrors(
      ...[INSIDE_SURVEY_INSIDE_UNITS, building, field].filter((x) => !!x)
    );
  }

  getValidationError(checkValidity?: boolean) {
    if (checkValidity)
      this.formValidationService.resetErrorsForPath(INSIDE_SURVEY_INSIDE_UNITS);
    let falseBuildings: { index: number; building: Building }[] = [];
    this.buildings.forEach((s, i) => {
      let unitsForBuildingInside = Object.values(s.floors).reduce(
        (u: number, i: Floor) => (u += i.totalNumberOfUnits),
        0
      );

      let unitsForBuildingOutside =
        s.outsideSurvey.numberOfBusinessUnits +
        s.outsideSurvey.numberOfLivingUnits +
        s.outsideSurvey.numberOfServiceUnits;

      if (unitsForBuildingOutside != unitsForBuildingInside) {
        falseBuildings.push({ index: i + 1, building: s });
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_INSIDE_UNITS,
            s.id,
            InsideSurveyInsideUnits.FLOORS_FIELD
          );
      }
    });
    if (falseBuildings.length) return this.warningToast(falseBuildings);
    else return undefined;
  }

  valueAscOrder = (
    a: KeyValue<number, string>,
    b: KeyValue<number, string>
  ): number => {
    return a.key - b.key;
  };

  warningToast = (falseBuildings: any) =>
    new ValidationError(
      $localize`:@@general.warning:Warning`,
      $localize`:@@validation.survey.unitsNotMatch: Total inside units should be equal to total outside units. Building${
        falseBuildings.length > 1 ? "s" : ""
      } ${falseBuildings.map((b, i) => b.index).join(" & ")}`,
      $localize`:@@survey.inside.inside.units:Inside Units`
    );

  throwErrorToast = () =>
    this._toastService.showWarnToast(
      $localize`:@@general.warning:Warning`,
      $localize`:@@validation.survey.savingFailed: Saving inside units has failed.`
    );
}
